"use client";

import { useState, useCallback } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PoapPreview } from "./PoapPreview";

interface PoapEventData {
  id: number;
  name: string;
  description: string;
  image_url: string;
  supply: number;
}

interface MintLinkStatus {
  total: number;
  used: number;
  available: number;
  imported: number;
}

interface PoapEventInputProps {
  clueIndex: number;
  poapEventId: string;
  onChange: (eventId: string) => void;
  clueId?: string;
}

export function PoapEventInput({ clueIndex, poapEventId, onChange, clueId }: PoapEventInputProps) {
  const [eventData, setEventData] = useState<PoapEventData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [secretCode, setSecretCode] = useState("");
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState("");
  const [mintLinkStatus, setMintLinkStatus] = useState<MintLinkStatus | null>(null);

  const fetchEventPreview = useCallback(async (eventId: string) => {
    if (!eventId.trim()) {
      setEventData(null);
      setError("");
      return;
    }

    setLoading(true);
    setError("");
    try {
      const response = await fetch(`/api/poap/event/${eventId}`);
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Event not found");
      }
      const data = await response.json();
      setEventData(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch event");
      setEventData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleImportMintLinks = async () => {
    if (!clueId || !poapEventId.trim() || !secretCode.trim()) return;

    setImporting(true);
    setImportError("");
    try {
      const response = await fetch("/api/poap/mint-links", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          eventId: poapEventId,
          clueId,
          secretCode,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to import mint links");
      }

      if (data.error) {
        setImportError(data.error);
      }

      setMintLinkStatus({
        total: data.total,
        used: data.used,
        available: data.available,
        imported: data.imported,
      });
    } catch (err) {
      setImportError(err instanceof Error ? err.message : "Failed to import mint links");
    } finally {
      setImporting(false);
    }
  };

  const importDisabled = !clueId || !poapEventId.trim() || !secretCode.trim() || importing;

  return (
    <div>
      <Label htmlFor={`clue-poap-event-${clueIndex}`}>POAP Event ID</Label>
      <div className="flex gap-3 items-start mt-1">
        <div className="flex-1">
          <Input
            id={`clue-poap-event-${clueIndex}`}
            value={poapEventId}
            onChange={(e) => onChange(e.target.value)}
            onBlur={(e) => fetchEventPreview(e.target.value)}
            placeholder="Enter POAP drop/event ID"
            className="font-mono"
          />
          <p className="text-xs text-gray-500 mt-1">
            Enter the POAP event ID from your POAP drop. The POAP will be claimed when a participant completes this location.
          </p>
          {error && (
            <p className="text-xs text-red-600 mt-1">{error}</p>
          )}
          {loading && (
            <p className="text-xs text-gray-500 mt-1">Loading event preview...</p>
          )}
        </div>
        {eventData && (
          <div className="flex-shrink-0">
            <PoapPreview
              imageUrl={eventData.image_url}
              name={eventData.name}
              supply={eventData.supply}
              size="sm"
            />
          </div>
        )}
      </div>

      {/* Secret Code + Import Mint Links */}
      <div className="mt-4">
        <Label htmlFor={`clue-poap-secret-${clueIndex}`}>Secret Code</Label>
        <div className="flex gap-2 items-start mt-1">
          <Input
            id={`clue-poap-secret-${clueIndex}`}
            value={secretCode}
            onChange={(e) => setSecretCode(e.target.value)}
            placeholder="POAP event secret code"
            className="flex-1 font-mono"
            type="password"
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleImportMintLinks}
            disabled={importDisabled}
            className="flex-shrink-0"
          >
            {importing ? "Importing..." : "Import Mint Links"}
          </Button>
        </div>
        {!clueId && (
          <p className="text-xs text-amber-600 mt-1">
            Save the campaign first to enable mint link import.
          </p>
        )}
        {clueId && (
          <p className="text-xs text-gray-500 mt-1">
            Enter the secret code from your POAP drop to import mint links for claiming.
          </p>
        )}
        {importError && (
          <p className="text-xs text-red-600 mt-1">{importError}</p>
        )}
        {mintLinkStatus && (
          <div className="mt-2 p-2 bg-green-50 border border-green-200 rounded-md">
            <p className="text-sm text-green-800">
              Imported {mintLinkStatus.imported} mint links.{" "}
              <span className="font-medium">
                {mintLinkStatus.available} available
              </span>{" "}
              / {mintLinkStatus.total} total ({mintLinkStatus.used} used)
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
